import { z } from 'zod';

import { alphanumericRegex, integerAndFloatNumbersWithNegativesRegex } from 'constants/regex';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import {
  anyBulkFormCategoryAllowsAttachments,
  parseRequestCategoryId,
  requestCategoryQualifiesForCreationAttachments,
} from 'helpers/categoryAllowsAttachments';

function fileListLength(list: unknown): number {
  if (list && typeof list === 'object' && 'length' in list) {
    return Number((list as { length: number }).length) || 0;
  }
  return 0;
}

const amountMaxValue = 99999.99;

function isFutureDate(date: Date): boolean {
  const currentDate = new Date();
  return date.getTime() > currentDate.getTime();
}

const validationSchema = (
  categoryRows: string[] = [],
  isMultipleTickets: boolean,
  ticketRequestCategories: TicketRequestCategoryI[] | undefined
): z.ZodTypeAny => {
  // Common validation schema for all cases
  const commonValidationSchema = {
    roofTopId: z.string({ required_error: 'Please select a dealership' }).uuid(),
    agentId: z.preprocess(
      v => (typeof v === 'string' && v.trim() === '' ? undefined : v),
      z.string({ required_error: 'Please select an agent' }).uuid('Please select an agent')
    ),
    roNumber: z
      .string()
      .min(1, 'Please enter a valid RO number')
      .regex(alphanumericRegex, { message: 'Only numbers or letters allowed' }),
    roDate: z
      .date({
        required_error: 'Please enter a valid RO open date',
        invalid_type_error: 'Please enter a valid RO open date',
      })
      .refine(selectedDate => !isFutureDate(selectedDate), {
        message: "Future date isn't allowed",
      }),
    claimSubmittedDate: z
      .date()
      .nullable()
      .optional()
      .refine(selectedDate => !selectedDate || !isFutureDate(selectedDate)),

    lineNumber: z.string().nullable().optional(),
    techNumber: z
      .string({ required_error: 'Please enter a tech number' })
      .min(1, 'Please enter a tech number'),
    advisorNumber: z
      .string({ required_error: 'Please enter an advisor number' })
      .min(1, 'Please enter an advisor number'),
    vin: z.string().nullable().optional(),
    amount: z
      .string()
      .min(1, 'Please enter a valid amount')
      .regex(integerAndFloatNumbersWithNegativesRegex, {
        message: 'Only integer or decimal numbers allowed',
      })
      .refine(
        val => {
          const amountValue = parseFloat(val);
          return amountValue <= amountMaxValue;
        },
        { message: 'Please enter an amount less than or equal to 99999.99' }
      )
      .refine(
        val => {
          const decimalPart = val.split('.')[1];
          return !decimalPart || decimalPart.length <= 2; // Allow up to 2 decimal places
        },
        { message: 'Please enter an amount with no more than 2 decimal places' }
      ),
    claimStatusId: z
      .number({ required_error: 'Please select a claim status' })
      .min(0, 'Please select a claim status'),
    ticketPriorityId: z
      .number({ required_error: 'Please select a priority' })
      .min(0, 'Please select a priority'),
    createMultipleTickets: z.boolean().optional(),
  };

  // Single ticket validation schema
  const creationAttachmentFieldsSchema = {
    creationAttachmentNote: z.string().optional(),
    creationAttachmentTypeCode: z.string().optional(),
    creationAttachmentFile: z.any().optional(),
    creationAttachmentPhoto: z.any().optional(),
  };

  const singleTicketValidationSchema = {
    requestCategoryId: z
      .number({ required_error: 'Please select a request category' })
      .min(0, 'Please select a request category'),
    requestSubcategoryId: z.preprocess(
      v => (v === null ? undefined : v),
      z
        .number({
          required_error: 'Subcategory is required',
          invalid_type_error: 'Subcategory is required',
        })
        .min(0, 'Subcategory is required')
    ),
    requestSubCategoryName: z.string().nullable().optional(),
    text: z.string().nullable(),
  };

  // Dynamic fields validation schema for multiple tickets
  const createMultipleCategoryValidationSchema = Object.fromEntries(
    categoryRows?.map(rowId => [
      `requestCategoryId-${rowId}`,
      z.number({ required_error: 'Category is required' }),
    ]) ?? []
  );
  const createMultipleSubcategoryValidationSchema = Object.fromEntries(
    categoryRows?.map(rowId => [
      `requestSubcategoryId-${rowId}`,
      z.preprocess(
        v => (v === null ? undefined : v),
        z.number({
          required_error: 'Subcategory is required',
          invalid_type_error: 'Subcategory is required',
        })
      ),
    ]) ?? []
  );
  const createMultipleTextValidationSchema = Object.fromEntries(
    categoryRows?.map(rowId => [`text-${rowId}`, z.string().optional()]) ?? []
  );

  const refineCreationRow = (
    val: Record<string, unknown>,
    ctx: z.RefinementCtx,
    rowId: string | null,
    attachmentsEnabled: boolean
  ): void => {
    const suffix = rowId == null ? '' : `-${rowId}`;
    const fileKey = `creationAttachmentFile${suffix}`;
    const photoKey = `creationAttachmentPhoto${suffix}`;
    const typeKey = `creationAttachmentTypeCode${suffix}`;

    const nFile = fileListLength(val[fileKey]);
    const nPhoto = fileListLength(val[photoKey]);
    const pathPrefix = rowId == null ? 'creationAttachmentFile' : fileKey;

    if (!attachmentsEnabled) {
      if (nFile > 0 || nPhoto > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Attachments are not allowed for this request category',
          path: [pathPrefix],
        });
      }
      return;
    }

    if (nFile === 0 && nPhoto === 0) {
      return;
    }
    if (nFile > 0 && nPhoto > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Not allowed to upload file and photo at the same time',
        path: [pathPrefix],
      });
      return;
    }
    const typeCode = String(val[typeKey] ?? '').trim();
    if (!typeCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Attachment type is required when adding a file',
        path: [rowId == null ? 'creationAttachmentTypeCode' : typeKey],
      });
    }
  };

  const singleTicketWithOptionalAttachmentsSchema = z
    .object({
      ...commonValidationSchema,
      ...singleTicketValidationSchema,
      ...creationAttachmentFieldsSchema,
    })
    .superRefine((val, ctx): void => {
      refineCreationRow(
        val as Record<string, unknown>,
        ctx,
        null,
        requestCategoryQualifiesForCreationAttachments(
          ticketRequestCategories,
          parseRequestCategoryId(val.requestCategoryId)
        )
      );
    });

  // Final schema based on ticket type
  const finalValidationSchema = isMultipleTickets
    ? z
        .object({
          ...commonValidationSchema,
          ...createMultipleCategoryValidationSchema,
          ...createMultipleSubcategoryValidationSchema,
          ...createMultipleTextValidationSchema,
          ...creationAttachmentFieldsSchema,
        })
        .superRefine((val, ctx): void => {
          const record = val as Record<string, unknown>;
          const bulkAttachmentsEnabled = anyBulkFormCategoryAllowsAttachments(
            ticketRequestCategories,
            categoryRows,
            record
          );
          refineCreationRow(record, ctx, null, bulkAttachmentsEnabled);
        })
    : singleTicketWithOptionalAttachmentsSchema;

  return finalValidationSchema;
};

export default validationSchema;
